import { useState } from "react";
import { currencySymbol } from "../lib/format";

const COMMON_CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "PLN", name: "Polish Złoty" },
  { code: "GBP", name: "British Pound" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "SEK", name: "Swedish Krona" },
  { code: "NOK", name: "Norwegian Krone" },
  { code: "CZK", name: "Czech Koruna" },
  { code: "UAH", name: "Ukrainian Hryvnia" },
];

interface Props {
  currentCurrency: string;
  onSelect: (currency: string) => Promise<void>;
  onClose: () => void;
}

export default function CurrencyPicker({ currentCurrency, onSelect, onClose }: Props) {
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSelect(code: string) {
    setSaving(true);
    setError(null);
    try {
      await onSelect(code);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
      setSaving(false);
    }
  }

  async function handleCustom() {
    const code = custom.trim().toUpperCase();
    if (!code || !/^[A-Za-z]{2,8}$/.test(code)) {
      setError("Enter a 2–8 letter ISO code (e.g. PLN)");
      return;
    }
    await handleSelect(code);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />
      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-bg border-t border-hairline rounded-t-2xl shadow-elevated px-4 pt-4 pb-8 max-h-[80vh] overflow-y-auto">
        <div className="w-10 h-1 rounded-full bg-fg/20 mx-auto mb-4" />
        <h2 className="font-sans text-base font-semibold text-fg text-center mb-4">
          Change currency
        </h2>

        {error && (
          <p className="text-danger text-sm text-center mb-3">{error}</p>
        )}

        {/* Common list */}
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden mb-4">
          {COMMON_CURRENCIES.map(({ code, name }) => (
            <button
              key={code}
              type="button"
              disabled={saving}
              onClick={() => void handleSelect(code)}
              className={`w-full flex items-center justify-between px-4 py-3.5 border-t border-hairline first:border-t-0 transition-colors ${
                code === currentCurrency ? "bg-surface-2" : "active:bg-surface-2"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-muted font-mono text-sm w-8">{currencySymbol(code)}</span>
                <div className="text-left">
                  <p className="text-fg text-sm font-medium">{code}</p>
                  <p className="text-muted text-xs">{name}</p>
                </div>
              </div>
              {code === currentCurrency && (
                <span className="text-fg text-sm font-bold">✓</span>
              )}
            </button>
          ))}
        </div>

        {/* Custom input */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Custom code (e.g. SGD)"
            value={custom}
            onChange={(e) => setCustom(e.target.value.toUpperCase().slice(0, 8))}
            className="flex-1 px-0 py-3 bg-transparent text-fg border-b border-hairline focus:outline-none focus:border-fg text-sm placeholder:text-faint"
            maxLength={8}
          />
          <button
            type="button"
            disabled={saving || !custom.trim()}
            onClick={() => void handleCustom()}
            className="px-5 py-3 rounded-xl bg-fg text-bg font-sans font-semibold text-sm active:opacity-70 transition-opacity disabled:opacity-40"
          >
            Set
          </button>
        </div>
      </div>
    </>
  );
}
